import { useCallback, useEffect, useState } from 'react';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import type { BrandProfile, BrandProfileInput } from '../lib/types';

export function useBrandProfile() {
  const [profile, setProfile] = useState<BrandProfile | null>(null);
  // The user whose profile has finished loading, so `loading` is already true on
  // the first render after sign-in, before the fetch has started.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const supabase = useSupabaseClient();
  const session = useSession();
  const userId = session?.user?.id ?? null;

  const fetchProfile = useCallback(async () => {
    if (!userId) {
      setProfile(null);
      setLoadedFor(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('brand_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);
    } catch (err) {
      // The tools work without a profile, so a failed load is not shown to the user.
      console.error('Error fetching brand profile:', err);
      setProfile(null);
    } finally {
      setLoadedFor(userId);
    }
  }, [userId, supabase]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const saveProfile = useCallback(async (values: BrandProfileInput) => {
    if (!userId) throw new Error('You need to be signed in to save your brand profile.');

    const { data, error } = await supabase
      .from('brand_profiles')
      .upsert({ user_id: userId, ...values })
      .select()
      .single();

    if (error) throw error;
    setProfile(data);
    return data as BrandProfile;
  }, [userId, supabase]);

  const loading = !!userId && loadedFor !== userId;

  return { profile: loading ? null : profile, loading, userId, saveProfile, refreshProfile: fetchProfile };
}
